// Sales Activity vocabulary registry — FE-SCRM-024.
//
// Single source of truth for human-facing labels per ActivityType.
// Keep this file aligned with backend/sales/models.py::ActivityType.
//
// `is_contact_event` is the FE mirror of the BE auto-Touchpoint rule
// (see `_ACTIVITY_TO_TOUCHPOINT_KIND` in backend/sales/services.py).
// We use it to surface "Will create a Touchpoint" copy in the
// composer so producers understand the side-effect of completing
// a call vs sending an RFP.

import type { ActivityType } from "./types";


export interface ActivityTypeMeta {
  value: ActivityType;
  label: string;
  is_contact_event: boolean;
}


// Order is intentional: the 4 quick-add types come first, then the
// fuller taxonomy roughly in the order a sales motion unfolds.
export const ACTIVITY_TYPE_META: ActivityTypeMeta[] = [
  { value: "note", label: "Note", is_contact_event: false },
  { value: "call", label: "Call", is_contact_event: true },
  { value: "meeting", label: "Meeting", is_contact_event: true },
  { value: "follow_up", label: "Follow-up", is_contact_event: false },
  { value: "email", label: "Email", is_contact_event: true },
  { value: "discovery_call", label: "Discovery call", is_contact_event: true },
  { value: "census_request", label: "Census request", is_contact_event: false },
  { value: "census_received", label: "Census received", is_contact_event: false },
  { value: "rfp_sent", label: "RFP sent", is_contact_event: false },
  { value: "proposals_received", label: "Proposals received", is_contact_event: false },
  { value: "carrier_meeting", label: "Carrier meeting", is_contact_event: true },
  { value: "proposal_delivered", label: "Proposal delivered", is_contact_event: false },
  { value: "finalist_presentation", label: "Finalist presentation", is_contact_event: true },
  { value: "bor_letter_generated", label: "BOR letter generated", is_contact_event: false },
  { value: "bor_letter_sent", label: "BOR letter sent", is_contact_event: false },
  { value: "decision_meeting", label: "Decision meeting", is_contact_event: true },
  { value: "contract_executed", label: "Contract executed", is_contact_event: false },
  { value: "win_loss_interview", label: "Win/loss interview", is_contact_event: true },
];


// The 4 most-used types surfaced as one-tap buttons. The remaining
// 14 live behind a "Log activity" menu.
export const QUICK_ADD_TYPES: ActivityType[] = [
  "note", "call", "meeting", "follow_up",
];


const _byValue = new Map<ActivityType, ActivityTypeMeta>(
  ACTIVITY_TYPE_META.map((m) => [m.value, m]),
);


export function activityTypeLabel(type: ActivityType): string {
  return _byValue.get(type)?.label ?? type;
}


export function isContactEventType(type: ActivityType): boolean {
  return _byValue.get(type)?.is_contact_event ?? false;
}
